import { create } from 'zustand';
import { RegisterRequest, RegisterRequestResponse, mapRegisterRequest } from '@/types/register';
import { api } from '@/lib/api/client';
import { AppError } from '@/utils/errors/AppError';

interface RegisterRequestState {
  requests: RegisterRequest[];
  isLoading: boolean;
  error: string | null;
  hasInitialFetch: boolean;
  
  fetchRequests: () => Promise<void>;
  approveRequest: (requestId: string, role: 'user' | 'admin') => Promise<void>;
  rejectRequest: (requestId: string) => Promise<void>;
  updateRequest: (requestId: string, updates: Partial<RegisterRequest>) => Promise<void>;
  reset: () => void;
}

export const useRegisterRequestStore = create<RegisterRequestState>((set, get) => ({
  requests: [],
  isLoading: false,
  error: null,
  hasInitialFetch: false,

  fetchRequests: async () => {
    const state = get();
    if (state.isLoading) return;
    
    const shouldShowLoading = !state.hasInitialFetch;
    if (shouldShowLoading) {
      set({ isLoading: true, error: null });
    }

    try {
      const response = await api.get<RegisterRequestResponse[]>('api/register-requests');
      
      // Map response data
      const mappedRequests = Array.isArray(response) ? response.map(mapRegisterRequest) : [];
      
      set({ 
        requests: mappedRequests,
        isLoading: false,
        error: null,
        hasInitialFetch: true
      });
    } catch (error) {
      console.error('Failed to fetch register requests:', error);
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to fetch register requests',
        hasInitialFetch: true
      });
    }
  },

  approveRequest: async (requestId: string, role: 'user' | 'admin') => {
    set({ isLoading: true, error: null });
    
    try {
      await api.post(`api/register-requests/${requestId}/approve`, { role });
      
      set(state => ({
        requests: state.requests.map(req =>
          req.id === requestId
            ? { ...req, status: 'approved', processedAt: new Date().toISOString() }
            : req
        ),
        isLoading: false,
        error: null
      }));
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to approve request'
      });
      throw error;
    }
  },

  rejectRequest: async (requestId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      await api.post(`api/register-requests/${requestId}/reject`);
      
      set(state => ({
        requests: state.requests.map(req =>
          req.id === requestId
            ? { ...req, status: 'rejected', processedAt: new Date().toISOString() }
            : req
        ),
        isLoading: false,
        error: null
      }));
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to reject request'
      });
      throw error;
    }
  },

  updateRequest: async (requestId: string, updates: Partial<RegisterRequest>) => {
    set({ isLoading: true, error: null });
  
    try {
      const response = await api.post(`api/register-requests/${requestId}/update`, updates);
  
      // Check if the API response indicates an error
      if (response.success === false) {
        throw new Error(response.error); // Throw the error message from the API
      }
  
      // Update the store on success
      set(state => ({
        requests: state.requests.map(req =>
          req.id === requestId
            ? { ...req, ...updates, updatedAt: new Date().toISOString() }
            : req
        ),
        isLoading: false,
        error: null
      }));
    } catch (error) {
      let errorMessage = 'Failed to update request';
  
      // Handle API error response
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
  
      set({ 
        isLoading: false,
        error: errorMessage
      });
      throw new Error(errorMessage); // Ensure the error is thrown with the correct message
    }
  },

  reset: () => {
    set({
      requests: [],
      isLoading: false,
      error: null,
      hasInitialFetch: false
    });
  }
}));